import React from "react";

/**
 * Cartoon-style endoscope logo mark for Dr. Kichian's Gastroenterology.
 * Uses brand teal + navy tokens so it looks native across light/dark surfaces.
 *
 * Props:
 *  - size: rendered box size in px (default 44)
 *  - className: additional wrapper classes
 *  - title: accessible title (defaults to a descriptive label)
 */
export function ScopeLogo({ size = 44, className = "", title = "Cartoon endoscope logo" }) {
    return (
        <span
            className={`inline-flex items-center justify-center ${className}`}
            style={{ width: size, height: size }}
            aria-hidden={false}
            role="img"
            aria-label={title}
            data-testid="brand-scope-logo"
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <title>{title}</title>
                {/* Soft background disc */}
                <circle
                    cx="32"
                    cy="32"
                    r="30"
                    fill="var(--brand-teal-soft, #d6efee)"
                />
                <circle
                    cx="32"
                    cy="32"
                    r="30"
                    stroke="var(--brand-teal-2, #0e7c86)"
                    strokeOpacity="0.35"
                    strokeWidth="1.5"
                />

                {/* Endoscope flexible cord — friendly curve */}
                <path
                    d="M14 14 C 14 26, 26 24, 30 34 C 34 44, 46 44, 50 36"
                    stroke="var(--brand-navy, #0f2747)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                {/* Cord highlight for cartoon feel */}
                <path
                    d="M15.5 15.5 C 15.5 24.5, 24.5 23.5, 28 32"
                    stroke="#ffffff"
                    strokeOpacity="0.5"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    fill="none"
                />

                {/* Handle/eyepiece at the top-left */}
                <rect
                    x="9"
                    y="8"
                    width="10"
                    height="10"
                    rx="2.5"
                    fill="var(--brand-navy, #0f2747)"
                />
                <circle cx="14" cy="13" r="2" fill="var(--brand-teal-2, #0e7c86)" />
                <circle cx="14" cy="13" r="0.9" fill="#ffffff" />

                {/* Scope tip / camera head at bottom-right */}
                <g>
                    <circle
                        cx="50"
                        cy="36"
                        r="6.5"
                        fill="var(--brand-teal-2, #0e7c86)"
                    />
                    <circle cx="50" cy="36" r="3.2" fill="var(--brand-navy, #0f2747)" />
                    <circle cx="51.2" cy="34.6" r="1.1" fill="#ffffff" />
                    {/* Light beam sparkle */}
                    <path
                        d="M57 36 L 60 34.5 M57 38 L 60 40 M56.5 36 L 60 36"
                        stroke="var(--brand-teal-2, #0e7c86)"
                        strokeOpacity="0.55"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                    />
                </g>

                {/* Little friendly heart to signal caring / patient-first */}
                <path
                    d="M42 15.5c0-1.6 1.3-3 3-3 1 0 1.9.6 2.4 1.4.5-.8 1.4-1.4 2.4-1.4 1.7 0 3 1.4 3 3 0 2.7-3.6 4.4-5.4 5.4-1.8-1-5.4-2.7-5.4-5.4z"
                    fill="var(--brand-teal-2, #0e7c86)"
                    opacity="0.9"
                />
            </svg>
        </span>
    );
}
